import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import PendingIcon from "@mui/icons-material/Pending";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import TimelineIcon from "@mui/icons-material/Timeline";
import VisibilityIcon from "@mui/icons-material/Visibility";

import api from "../../services/api";
import { EmptyState, LoadingState, PageHeader, PageLayout } from "../../components/ui";

const API = {
  appraisals: "/performance/appraisals",
  statsMine: "/performance/appraisals/stats/mine",
};

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
  submitted: "Submitted",
  not_started: "Not started",
  unassigned: "Unassigned",
  skipped: "Skipped",
};

const STATUS_COLOR = {
  draft: "default",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  cancelled: "error",
  submitted: "success",
  not_started: "default",
  unassigned: "error",
  skipped: "default",
};

const safeMsg = (err, fallback = "Failed to load appraisals") =>
  err?.response?.data?.message || err?.message || fallback;

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const numOrNull = (value) => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pickFirst = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
};

const getSteps = (row) => {
  const steps = row?.step_instances || row?.stepInstances || [];
  return Array.isArray(steps) ? steps : [];
};

const getCurrentStep = (row) => {
  const steps = getSteps(row);
  if (!steps.length) return null;

  const currentOrder = row?.current_step_order;
  let current = null;

  if (currentOrder != null) {
    current = steps.find((step) => Number(step.step_order) === Number(currentOrder));
  }

  if (!current) {
    current = [...steps]
      .sort((a, b) => Number(a.step_order || 0) - Number(b.step_order || 0))
      .find((step) => ["pending", "not_started", "in_progress"].includes(String(step.status)));
  }

  return current || steps[steps.length - 1] || null;
};

const getProgressInfo = (row) => {
  const steps = getSteps(row);
  if (!steps.length) return { completed: 0, total: 0, percent: 0 };

  const total = steps.length;
  const completed = steps.filter((step) =>
    ["completed", "submitted"].includes(String(step.status || "").toLowerCase())
  ).length;

  return {
    completed,
    total,
    percent: total ? Math.round((completed / total) * 100) : 0,
  };
};

const getStepIcon = (status) => {
  const key = String(status || "").toLowerCase();
  if (["completed", "submitted"].includes(key)) return <CheckCircleIcon color="success" fontSize="small" />;
  if (["pending", "in_progress"].includes(key)) return <PendingIcon color="warning" fontSize="small" />;
  if (key === "unassigned") return <InfoIcon color="error" fontSize="small" />;
  return <ScheduleIcon color="action" fontSize="small" />;
};

const statusLabel = (status) => {
  const key = String(status || "").toLowerCase();
  return STATUS_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) || "-";
};

const getAttribute = (row, key) => {
  if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  if (row?.attributes?.[key] !== undefined && row?.attributes?.[key] !== null) return row.attributes[key];
  return null;
};

const getStepLabel = (step) =>
  step?.step_name || step?.title || step?.name || step?.label || `Step ${step?.step_order || "-"}`;

const getRatingInfo = (row) => {
  const currentOverall = pickFirst(
    row?.overall_rating,
    row?.overallRating,
    row?.overall_details?.overall_rating,
    row?.overallDetails?.overall_rating
  );
  const previousOverall = pickFirst(
    row?.previous_overall_rating,
    row?.previous_overall,
    row?.previousOverallRating,
    row?.previousOverall,
    row?.prev_overall_rating,
    row?.prevOverallRating
  );
  const currentNumber = numOrNull(currentOverall);
  const previousNumber = numOrNull(previousOverall);
  const delta =
    currentNumber == null || previousNumber == null
      ? null
      : Math.round((currentNumber - previousNumber) * 100) / 100;

  return { currentOverall, previousOverall, delta };
};

function StatusPill({ value, size = "small" }) {
  const key = String(value || "").toLowerCase();
  return (
    <Chip
      size={size}
      color={STATUS_COLOR[key] || "default"}
      label={statusLabel(key)}
      variant="filled"
      sx={{ borderRadius: "8px", fontWeight: 800, border: "1px solid", borderColor: "divider" }}
    />
  );
}

function SnapshotItem({ label, value, helper, icon, color = "primary.main", loading = false }) {
  const theme = useTheme();
  const [paletteKey, shadeKey] = String(color).split(".");
  const accent = theme.palette?.[paletteKey]?.[shadeKey] || theme.palette.primary.main;

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 0.9,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: alpha(accent, theme.palette.mode === "dark" ? 0.26 : 0.16),
        bgcolor: theme.palette.mode === "dark" ? alpha(accent, 0.08) : alpha("#ffffff", 0.74),
      }}
    >
      <Stack direction="row" spacing={0.9} alignItems="center">
        <Box
          sx={{
            width: 30,
            height: 30,
            flex: "0 0 auto",
            display: "grid",
            placeItems: "center",
            borderRadius: "8px",
            color: accent,
            bgcolor: alpha(accent, theme.palette.mode === "dark" ? 0.2 : 0.1),
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.05, fontWeight: 900 }}>
            {loading ? <CircularProgress size={18} /> : value}
          </Typography>
          {helper ? (
            <Typography noWrap variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function AppraisalSnapshot({
  stats,
  actionRequiredCount,
  visibleCount,
  totalRows,
  breakdownText,
  statusFilter,
  loading,
  statsLoading,
}) {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1, md: 1.15 },
        borderRadius: "8px",
        overflow: "hidden",
        borderColor: theme.hr?.line?.soft || "divider",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.42)}, ${alpha(theme.palette.background.paper, 0.92)})`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.075)}, ${alpha(theme.palette.secondary.main, 0.055)} 55%, ${alpha("#ffffff", 0.92)})`,
        boxShadow: "none",
      }}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.2} alignItems={{ lg: "center" }}>
        <Box sx={{ minWidth: 0, flex: "1 1 260px" }}>
          <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label="Personal cycle"
              color="primary"
              variant="outlined"
              sx={{ borderRadius: "8px" }}
            />
            {statusFilter ? (
              <StatusPill value={statusFilter} />
            ) : (
              <Chip size="small" label="All statuses" variant="outlined" sx={{ borderRadius: "8px" }} />
            )}
          </Stack>
          <Typography variant="subtitle1" sx={{ mt: 0.6, fontWeight: 900 }}>
            Appraisal overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
            {breakdownText}. Showing {visibleCount} of {totalRows || 0} records in your appraisal history.
          </Typography>
        </Box>

        <Box
          sx={{
            flex: "2 1 560px",
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: 0.7,
          }}
        >
          <SnapshotItem
            label="In progress"
            value={stats.mine_in_progress ?? 0}
            helper="Open cycles"
            icon={<PendingIcon fontSize="small" />}
            color="info.main"
            loading={statsLoading}
          />
          <SnapshotItem
            label="Completed"
            value={stats.mine_completed ?? 0}
            helper="Finished cycles"
            icon={<CheckCircleIcon fontSize="small" />}
            color="success.main"
            loading={statsLoading}
          />
          <SnapshotItem
            label="Action required"
            value={actionRequiredCount}
            helper="On this page"
            icon={<InfoIcon fontSize="small" />}
            color="warning.main"
            loading={loading}
          />
          <SnapshotItem
            label="Total"
            value={stats.mine_total ?? totalRows ?? 0}
            helper="All appraisals"
            icon={<TimelineIcon fontSize="small" />}
            color="primary.main"
            loading={statsLoading}
          />
        </Box>
      </Stack>
    </Paper>
  );
}

function ProgressMeter({ progress }) {
  const theme = useTheme();
  if (!progress.total) return <Typography variant="caption" color="text.secondary">No workflow</Typography>;

  return (
    <Box sx={{ minWidth: 170 }}>
      <LinearProgress
        variant="determinate"
        value={progress.percent}
        sx={{
          height: 6,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.primary.main, 0.09),
          "& .MuiLinearProgress-bar": { borderRadius: 999 },
        }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.45 }}>
        <Typography variant="caption" color="text.secondary">
          {progress.completed}/{progress.total} steps
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 850, color: "primary.main" }}>
          {progress.percent}%
        </Typography>
      </Stack>
    </Box>
  );
}

function DetailBox({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {value || "-"}
      </Typography>
    </Box>
  );
}

function ExpandedDetails({ row }) {
  const theme = useTheme();
  const steps = useMemo(
    () => [...getSteps(row)].sort((a, b) => Number(a.step_order || 0) - Number(b.step_order || 0)),
    [row]
  );
  const currentStep = getCurrentStep(row);
  const rating = getRatingInfo(row);

  return (
    <Box
      sx={{
        p: { xs: 1.2, md: 1.5 },
        bgcolor: theme.palette.mode === "dark" ? alpha("#ffffff", 0.035) : alpha(theme.palette.primary.main, 0.025),
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.35fr 0.65fr" },
          gap: 1.2,
        }}
      >
        <Paper variant="outlined" sx={{ p: 1.4, borderRadius: "8px", borderColor: theme.hr?.line?.soft || "divider" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TimelineIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Workflow timeline
            </Typography>
          </Stack>

          <List dense disablePadding>
            {steps.length ? (
              steps.map((step, index) => {
                const key = String(step.status || "").toLowerCase();
                const isCurrent = step.id === currentStep?.id;
                const isCompleted = ["completed", "submitted"].includes(key);

                return (
                  <ListItem
                    key={step.id || index}
                    sx={{
                      alignItems: "flex-start",
                      mb: 0.85,
                      px: 1,
                      py: 0.85,
                      borderRadius: "8px",
                      border: "1px solid",
                      borderColor: isCurrent ? alpha(theme.palette.primary.main, 0.32) : theme.hr?.line?.soft || "divider",
                      bgcolor: isCurrent ? alpha(theme.palette.primary.main, 0.055) : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 38, mt: 0.2 }}>
                      <Badge
                        badgeContent={step.step_order || index + 1}
                        color={isCurrent ? "primary" : isCompleted ? "success" : "default"}
                      >
                        {getStepIcon(step.status)}
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ component: "div" }}
                      secondaryTypographyProps={{ component: "div" }}
                      primary={
                        <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                          <Typography variant="body2" sx={{ fontWeight: isCurrent ? 900 : 750 }}>
                            {getStepLabel(step)}
                          </Typography>
                          {isCurrent ? (
                            <Chip
                              size="small"
                              label="Current"
                              color="primary"
                              variant="outlined"
                              sx={{ borderRadius: "8px" }}
                            />
                          ) : null}
                          <StatusPill value={step.status} />
                        </Stack>
                      }
                      secondary={
                        <Box sx={{ mt: 0.35 }}>
                          <Typography variant="caption" color="text.secondary">
                            {step.user?.name ? `Assigned to ${step.user.name}` : "Unassigned"}
                          </Typography>
                          {step.submitted_at ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                              Submitted {formatDateTime(step.submitted_at)}
                            </Typography>
                          ) : null}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                Workflow steps will appear when this appraisal starts.
              </Typography>
            )}
          </List>
        </Paper>

        <Stack spacing={1.2}>
          <Paper variant="outlined" sx={{ p: 1.4, borderRadius: "8px", borderColor: theme.hr?.line?.soft || "divider" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
              Appraisal details
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.2,
              }}
            >
              <DetailBox label="Employee code" value={row.employee?.employee_code || row.employee_code} />
              <DetailBox label="Department" value={row.employee?.department} />
              <DetailBox label="Current step order" value={row.current_step_order} />
              <DetailBox label="Template" value={row.workflow_template?.slug || row.workflowTemplate?.slug} />
              <DetailBox label="Overall rating" value={rating.currentOverall} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Previous rating
                </Typography>
                <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {rating.previousOverall || "-"}
                  </Typography>
                  {rating.delta != null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Change ${rating.delta > 0 ? "+" : ""}${rating.delta}`}
                      sx={{ borderRadius: "8px" }}
                    />
                  ) : null}
                </Stack>
              </Box>
              <DetailBox label="Created" value={formatDateTime(row.created_at)} />
              <DetailBox label="Last updated" value={formatDateTime(row.updated_at)} />
            </Box>
          </Paper>

          {row.my_task_state ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.4,
                borderRadius: "8px",
                borderColor:
                  row.my_task_state === "action_required"
                    ? alpha(theme.palette.warning.main, 0.28)
                    : alpha(theme.palette.success.main, 0.24),
                bgcolor:
                  row.my_task_state === "action_required"
                    ? alpha(theme.palette.warning.main, 0.08)
                    : alpha(theme.palette.success.main, 0.07),
              }}
            >
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <InfoIcon
                  color={row.my_task_state === "action_required" ? "warning" : "success"}
                  fontSize="small"
                />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                    Your involvement
                  </Typography>
                  <Typography variant="body2">
                    {row.my_task_state === "action_required"
                      ? "Action required from you"
                      : row.my_task_state === "completed_by_me"
                        ? "You have completed your part"
                        : "Not currently assigned"}
                  </Typography>
                  {row.my_last_action_at ? (
                    <Typography variant="caption" color="text.secondary">
                      Last action: {formatDateTime(row.my_last_action_at)}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

function AppraisalCards({ rows, expandedRow, onExpand, onView }) {
  const theme = useTheme();

  return (
    <Stack spacing={1}>
      {rows.map((row) => {
        const currentStep = getCurrentStep(row);
        const progress = getProgressInfo(row);
        const period = getAttribute(row, "period");
        const cycle = getAttribute(row, "cycle");
        const rating = getRatingInfo(row);
        const isExpanded = expandedRow === row.id;

        return (
          <Paper
            key={row.id}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              overflow: "hidden",
              borderColor: theme.hr?.line?.soft || "divider",
            }}
          >
            <Box sx={{ p: 1.25 }}>
              <Stack spacing={1}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {row?.workflow_template?.name || row?.workflowTemplate?.name || "Appraisal cycle"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{row.id} - {period || "-"} - {cycle || "-"}
                    </Typography>
                  </Box>
                  <StatusPill value={row.status} />
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <DetailBox label="Rating" value={rating.currentOverall} />
                  <DetailBox label="Previous" value={rating.previousOverall} />
                </Box>

                <Stack spacing={0.6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    Progress
                  </Typography>
                  <ProgressMeter progress={progress} />
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  Current step: {currentStep ? getStepLabel(currentStep) : "-"}
                </Typography>

                <Stack direction="row" spacing={0.8} justifyContent="space-between">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onExpand(row.id)}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ borderRadius: "8px" }}
                  >
                    {isExpanded ? "Hide details" : "Show details"}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onView(row.id)}
                    sx={{ borderRadius: "8px" }}
                  >
                    View
                  </Button>
                </Stack>
              </Stack>
            </Box>
            {isExpanded ? <ExpandedDetails row={row} /> : null}
          </Paper>
        );
      })}
    </Stack>
  );
}

export default function MyAppraisalsPage() {
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [stats, setStats] = useState({
    mine_in_progress: 0,
    mine_completed: 0,
    mine_total: 0,
    breakdown: {},
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const actionRequiredCount = useMemo(
    () => rows.filter((row) => row?.my_task_state === "action_required").length,
    [rows]
  );

  const breakdownText = useMemo(() => {
    const entries = Object.entries(stats?.breakdown || {}).filter(([, value]) => Number(value) > 0);
    if (!entries.length) return "No status breakdown";
    return entries.map(([key, value]) => `${value} ${statusLabel(key)}`).join(", ");
  }, [stats]);

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (id) => navigate(`/performance/appraisals/${id}`);
  const handleRowExpand = (id) => setExpandedRow((current) => (current === id ? null : id));

  const fetchAppraisals = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        status: statusFilter || undefined,
        mine: 1,
        with_details: 1,
      };

      const res = await api.get(API.appraisals, { params });
      const payload = res?.data || {};
      setRows(Array.isArray(payload?.data) ? payload.data : []);
      setTotalRows(Number(payload?.total || 0));
    } catch (err) {
      console.error("Failed to load appraisals", err);
      setError(safeMsg(err));
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const res = await api.get(API.statsMine);
      setStats(res?.data || {});
    } catch (err) {
      console.error("Failed to load stats", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const handleRefresh = () => {
    fetchAppraisals();
    fetchStats();
  };

  useEffect(() => {
    fetchAppraisals();
    fetchStats();
  }, [fetchAppraisals, fetchStats]);

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        overline="Performance workspace"
        title="My appraisals"
        subtitle="Review your appraisal cycles, ratings, workflow progress, and pending actions."
        actions={[
          <Button
            key="refresh"
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading || statsLoading}
            sx={{ borderRadius: "8px" }}
          >
            Refresh
          </Button>,
        ]}
      />

      <Stack spacing={1}>
        <AppraisalSnapshot
          stats={stats}
          actionRequiredCount={actionRequiredCount}
          visibleCount={rows.length}
          totalRows={totalRows}
          breakdownText={breakdownText}
          statusFilter={statusFilter}
          loading={loading}
          statsLoading={statsLoading}
        />

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 1, md: 1.15 },
            borderRadius: "8px",
            borderColor: theme.hr?.line?.soft || "divider",
            backgroundColor: alpha(theme.palette.background.paper, 0.96),
            boxShadow: "none",
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <TextField
              size="small"
              select
              label="Status"
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(0);
              }}
              sx={{
                minWidth: { xs: "100%", sm: 220 },
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
              }}
              disabled={loading}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="text"
              color="inherit"
              onClick={() => {
                setStatusFilter("");
                setPage(0);
              }}
              disabled={loading || !statusFilter}
              sx={{ borderRadius: "8px" }}
            >
              Clear filter
            </Button>
            <Chip
              icon={<InfoIcon />}
              label={`Showing ${rows.length} of ${totalRows}`}
              size="small"
              variant="outlined"
              sx={{ ml: { md: "auto" }, borderRadius: "8px" }}
            />
          </Stack>
        </Paper>

        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: "8px",
            borderColor: theme.hr?.line?.soft || "divider",
            overflow: "hidden",
            backgroundColor: alpha(theme.palette.background.paper, 0.98),
            boxShadow: theme.hr?.shadow?.soft,
          }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              <LoadingState rows={8} />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <EmptyState
                compact
                title="No appraisals found"
                description={
                  statusFilter
                    ? `No appraisals with status "${STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label}".`
                    : "You do not have any appraisals yet."
                }
              />
            </Box>
          ) : mdUp ? (
            <TableContainer sx={{ overflow: "auto" }}>
              <Table stickyHeader size="small" aria-label="My appraisals table">
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        py: 1.15,
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? alpha("#ffffff", 0.04)
                            : alpha(theme.palette.primary.main, 0.045),
                        color: "text.secondary",
                        fontWeight: 900,
                        fontSize: theme.typography.pxToRem(12),
                        textTransform: "none",
                      },
                    }}
                  >
                    <TableCell width={44} />
                    <TableCell>Cycle</TableCell>
                    <TableCell width={140}>Period</TableCell>
                    <TableCell width={150}>Rating</TableCell>
                    <TableCell width={130}>Status</TableCell>
                    <TableCell>Current step</TableCell>
                    <TableCell width={210}>Progress</TableCell>
                    <TableCell width={125}>Updated</TableCell>
                    <TableCell width={92} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const currentStep = getCurrentStep(row);
                    const progress = getProgressInfo(row);
                    const period = getAttribute(row, "period");
                    const cycle = getAttribute(row, "cycle");
                    const label = getAttribute(row, "label");
                    const rating = getRatingInfo(row);
                    const isExpanded = expandedRow === row.id;

                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          hover
                          sx={{
                            bgcolor: isExpanded ? alpha(theme.palette.primary.main, 0.025) : "inherit",
                            "& td": { py: 0.9, borderBottomColor: theme.hr?.line?.soft || "divider" },
                          }}
                        >
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleRowExpand(row.id)}
                              sx={{
                                transform: isExpanded ? "rotate(180deg)" : "none",
                                transition: "transform 160ms ease",
                                borderRadius: "8px",
                              }}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ minWidth: 260 }}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                {row?.workflow_template?.name || row?.workflowTemplate?.name || "Appraisal cycle"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                #{row.id} {label ? `- ${label}` : ""} {cycle ? `- ${cycle}` : ""}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" label={period || "-"} sx={{ borderRadius: "8px" }} />
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                                {rating.currentOverall || "-"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Previous {rating.previousOverall || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <StatusPill value={row.status} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            {currentStep ? (
                              <Stack spacing={0.25}>
                                <Stack direction="row" spacing={0.7} alignItems="center">
                                  {getStepIcon(currentStep.status)}
                                  <Typography variant="body2" sx={{ fontWeight: 750 }}>
                                    {getStepLabel(currentStep)}
                                  </Typography>
                                </Stack>
                                {currentStep.user?.name ? (
                                  <Typography variant="caption" color="text.secondary">
                                    <PersonIcon fontSize="inherit" sx={{ verticalAlign: "middle", mr: 0.4 }} />
                                    {currentStep.user.name}
                                  </Typography>
                                ) : null}
                              </Stack>
                            ) : (
                              <Typography variant="caption" color="text.secondary">No current step</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <ProgressMeter progress={progress} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">{formatDateOnly(row.updated_at)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => handleView(row.id)}
                                sx={{ borderRadius: "8px" }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {isExpanded ? (
                          <TableRow>
                            <TableCell colSpan={9} sx={{ p: 0, borderBottomColor: theme.hr?.line?.soft || "divider" }}>
                              <ExpandedDetails row={row} />
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ p: 1.1 }}>
              <AppraisalCards rows={rows} expandedRow={expandedRow} onExpand={handleRowExpand} onView={handleView} />
            </Box>
          )}

          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ borderTop: "1px solid", borderTopColor: theme.hr?.line?.soft || "divider" }}
          />
        </Paper>
      </Stack>
    </PageLayout>
  );
}
